import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /**
   * Set a value in localStorage
   * @param key The key to store the value under
   * @param value The value to store
   * @returns boolean indicating success
   */
  setItem<T>(key: string, value: T): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }

  /**
   * Get a value from localStorage
   * @param key The key to retrieve
   * @param defaultValue The default value to return if key doesn't exist
   * @returns The stored value or default value
   */
  getItem<T>(key: string, defaultValue: T): T {
    if (!isPlatformBrowser(this.platformId)) {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Remove a value from localStorage
   * @param key The key to remove
   * @returns boolean indicating success
   */
  removeItem(key: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }

  /**
   * Clear all items from localStorage
   * @returns boolean indicating success
   */
  clear(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Check if a key exists in localStorage
   * @param key The key to check
   * @returns boolean indicating if key exists
   */
  hasKey(key: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error('Error checking localStorage key:', error);
      return false;
    }
  }
}
